import { useMemo } from 'react';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { PROCESS_MANAGEMENT_COLUMN_TYPES } from 'common/types/summaryDefinitions';
import type { DashboardFieldAvailability } from 'pages/Intelligence/components/SummaryDashboardEditDialog';
import { getObjectValue } from 'utilities';

export type IntelligenceSummaryType = 'firm' | 'valuations';

interface ProcessManagementGateParams {
  currentFirm: { show_process_management_column?: boolean };
  // Optional because some callers hold it as a possibly-undefined prop; an absent summary type is
  // not the valuations page, so the gate stays closed.
  summaryType: IntelligenceSummaryType | undefined;
}

/**
 * Whether Process Management columns may be offered for this summary.
 *
 * The firm flag is an availability gate, and it only applies to the valuations page. Shared by every
 * consumer — the spreadsheet column editor, the spreadsheet Add Columns modal, the spreadsheet
 * render path and the dashboard edit dialog — because two of them were already carrying a "keep in
 * sync" comment between copies.
 */
export const isProcessManagementColumnEnabled = ({
  currentFirm,
  summaryType,
}: ProcessManagementGateParams): boolean =>
  summaryType === 'valuations' && Boolean(currentFirm.show_process_management_column);

/**
 * Which dashboard fields this user's firm may configure.
 *
 * Returns a set of column names to hide from every picker, so the caller can pass it into
 * `buildComponentSections` — the section builders stay pure, and reading the store inside the
 * dialog would both break its provider-less tests and make them untestable.
 *
 * Deliberately not role-aware. Auditor restriction across this app is row-level: `apply_auditor_scope`
 * drops firm and fund-rollup rows and keeps only ready-for-audit companies, and the dashboard data
 * path reads company and company-rollup rows only. Every fund-scoped metric is written onto the
 * per-fund company rows an auditor does see, so there is no column an auditor cannot resolve —
 * hiding fields by role would hide fields that work.
 *
 * Also deliberately not gated on feature-flag hydration: flags read OFF for the first frame, and the
 * whole dashboard view kind already sits behind its flag at the route level.
 */
const useDashboardFieldAvailability = (
  summaryType: IntelligenceSummaryType
): DashboardFieldAvailability => {
  const [storeValue] = useStore() as UseStoreValues;
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  const isProcessManagementEnabled = isProcessManagementColumnEnabled({
    currentFirm,
    summaryType,
  });

  return useMemo(
    () => ({
      excludedFields: isProcessManagementEnabled
        ? new Set<string>()
        : new Set<string>(PROCESS_MANAGEMENT_COLUMN_TYPES),
    }),
    [isProcessManagementEnabled]
  );
};

export default useDashboardFieldAvailability;
